import React from "react";
import { getApiUrl, getRelativeUrl } from "../../utils/urlHelper";

const WatermarkSettingsTab = ({
  wmEnabled,
  setWmEnabled,
  isWmDragging,
  setIsWmDragging,
  handleWmDrop,
  handleWmFileChange,
  wmPosition,
  applyPresetPosition,
  groupMedia,
  selectedSamplePhotoIndex,
  setSelectedSamplePhotoIndex,
  wmCanvasRef,
  handleWmCanvasMouseDown,
  handleWmCanvasMouseMove,
  handleWmCanvasMouseUp,
  wmPreviewUrl,
  wmScale,
  setWmScale,
  wmOpacity,
  setWmOpacity,
  wmPosX,
  setWmPosX,
  wmPosY,
  setWmPosY,
  setWmPosition,
  handleSaveWatermark,
  isSaving,
}) => {
  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <div className="settings-card-icon">💧</div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <h2 className="settings-card-title">Photo Watermark Settings</h2>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 700,
              color: wmEnabled ? "#16a34a" : "#64748b",
            }}
          >
            <input
              type="checkbox"
              checked={wmEnabled}
              onChange={(e) => setWmEnabled(e.target.checked)}
              style={{
                width: "18px",
                height: "18px",
                accentColor: "#16a34a",
                cursor: "pointer",
              }}
            />
            <span>
              {wmEnabled ? "✅ Watermark Active" : "❌ Watermark Disabled"}
            </span>
          </label>
        </div>
      </div>

      <div className="cover-settings-grid">
        {/* Left Column: Upload Zone & Presets */}
        <div>
          <div className="settings-form-group">
            <label className="settings-label">
              Upload Watermark Logo (PNG/Image)
            </label>
            <label
              className={`settings-dropzone ${isWmDragging ? "dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsWmDragging(true);
              }}
              onDragLeave={() => setIsWmDragging(false)}
              onDrop={handleWmDrop}
            >
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  e.target.files[0] && handleWmFileChange(e.target.files[0])
                }
              />
              <span style={{ fontSize: "28px", marginBottom: "6px" }}>💧</span>
              <strong style={{ color: "#0f172a", fontSize: "14px" }}>
                Click or Drag & Drop Watermark Logo
              </strong>
              <span
                style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}
              >
                Upload transparent PNG logo/branding to overlay on photos
              </span>
            </label>
          </div>

          <div className="settings-form-group">
            <label className="settings-label">Quick Position Presets</label>
            <div className="watermark-presets-grid">
              <button
                type="button"
                className={`watermark-preset-btn ${
                  wmPosition === "top-left" ? "active" : ""
                }`}
                onClick={() => applyPresetPosition("top-left")}
              >
                ↖ Top Left
              </button>
              <button
                type="button"
                className={`watermark-preset-btn ${
                  wmPosition === "top-right" ? "active" : ""
                }`}
                onClick={() => applyPresetPosition("top-right")}
              >
                ↗ Top Right
              </button>
              <button
                type="button"
                className={`watermark-preset-btn ${
                  wmPosition === "center" ? "active" : ""
                }`}
                onClick={() => applyPresetPosition("center")}
              >
                🎯 Center
              </button>
              <button
                type="button"
                className={`watermark-preset-btn ${
                  wmPosition === "bottom-left" ? "active" : ""
                }`}
                onClick={() => applyPresetPosition("bottom-left")}
              >
                ↙ Bottom Left
              </button>
              <button
                type="button"
                className={`watermark-preset-btn ${
                  wmPosition === "bottom-right" ? "active" : ""
                }`}
                onClick={() => applyPresetPosition("bottom-right")}
              >
                ↘ Bottom Right
              </button>
              <button
                type="button"
                className={`watermark-preset-btn ${
                  wmPosition === "tile" ? "active" : ""
                }`}
                onClick={() => setWmPosition("tile")}
              >
                🏁 Repeat (Grid)
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Interactive Drag-to-Position Canvas & Fine-tune Sliders */}
        <div>
          <label
            className="settings-label"
            style={{ textAlign: "center", marginBottom: "6px" }}
          >
            🖱️ Live Photo Watermark Preview (Drag Watermark to Position)
          </label>

          {/* Real Group Photo Switcher Bar */}
          {groupMedia.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                overflowX: "auto",
                padding: "2px 0 8px",
                justifyContent: "center",
              }}
            >
              {groupMedia.slice(0, 10).map((item, idx) => (
                <img
                  key={idx}
                  src={getApiUrl(getRelativeUrl(item.filePath))}
                  alt={`Group Item ${idx + 1}`}
                  onClick={() => setSelectedSamplePhotoIndex(idx)}
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "8px",
                    objectFit: "cover",
                    cursor: "pointer",
                    border:
                      selectedSamplePhotoIndex === idx
                        ? "2.5px solid #6366f1"
                        : "2px solid rgba(0,0,0,0.1)",
                    opacity: selectedSamplePhotoIndex === idx ? 1 : 0.55,
                    transition: "all 0.2s ease",
                  }}
                  title={`Click to preview watermark on Group Photo #${idx + 1}`}
                />
              ))}
            </div>
          )}

          <div
            ref={wmCanvasRef}
            className="watermark-canvas-container"
            onMouseDown={handleWmCanvasMouseDown}
            onMouseMove={handleWmCanvasMouseMove}
            onMouseUp={handleWmCanvasMouseUp}
            onMouseLeave={handleWmCanvasMouseUp}
            onTouchStart={handleWmCanvasMouseDown}
            onTouchMove={handleWmCanvasMouseMove}
            onTouchEnd={handleWmCanvasMouseUp}
          >
            <img
              src={
                groupMedia.length > 0 &&
                groupMedia[selectedSamplePhotoIndex]?.filePath
                  ? getApiUrl(
                      getRelativeUrl(
                        groupMedia[selectedSamplePhotoIndex].filePath
                      )
                    )
                  : "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&auto=format&fit=crop"
              }
              alt="Sample Preview"
              className="watermark-sample-photo"
            />

            {/* Render Watermark Overlay on Sample Canvas */}
            {wmPreviewUrl ? (
              wmPosition === "tile" ? (
                <div className="watermark-tile-grid">
                  {[...Array(9)].map((_, idx) => (
                    <div key={idx} className="watermark-tile-item">
                      <img
                        src={wmPreviewUrl}
                        alt="WM Tile"
                        style={{
                          width: `${wmScale * 1.5}%`,
                          opacity: wmOpacity / 100,
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="watermark-drag-overlay"
                  style={{
                    left: `${wmPosX}%`,
                    top: `${wmPosY}%`,
                    width: `${wmScale}%`,
                  }}
                >
                  <img
                    src={wmPreviewUrl}
                    alt="Watermark Overlay"
                    style={{
                      width: "100%",
                      height: "auto",
                      opacity: wmOpacity / 100,
                      objectFit: "contain",
                      display: "block",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              )
            ) : (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.5)",
                  color: "white",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Upload a watermark PNG logo to preview
              </div>
            )}
          </div>

          {/* Fine-Tuning Sliders */}
          <div className="settings-slider-group">
            <div className="settings-slider-row">
              <div className="settings-slider-header">
                <span>Watermark Size (Scale)</span>
                <span>{wmScale}%</span>
              </div>
              <input
                type="range"
                min="5"
                max="50"
                className="settings-slider-input"
                value={wmScale}
                onChange={(e) => setWmScale(parseFloat(e.target.value))}
              />
            </div>

            <div className="settings-slider-row">
              <div className="settings-slider-header">
                <span>Watermark Opacity (Transparency)</span>
                <span>{wmOpacity}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="100"
                className="settings-slider-input"
                value={wmOpacity}
                onChange={(e) => setWmOpacity(parseFloat(e.target.value))}
              />
            </div>

            {wmPosition !== "tile" && (
              <>
                <div className="settings-slider-row">
                  <div className="settings-slider-header">
                    <span>Horizontal Position (X)</span>
                    <span>{Math.round(wmPosX)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    className="settings-slider-input"
                    value={wmPosX}
                    onChange={(e) => {
                      setWmPosX(parseFloat(e.target.value));
                      setWmPosition("custom");
                    }}
                  />
                </div>

                <div className="settings-slider-row">
                  <div className="settings-slider-header">
                    <span>Vertical Position (Y)</span>
                    <span>{Math.round(wmPosY)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    className="settings-slider-input"
                    value={wmPosY}
                    onChange={(e) => {
                      setWmPosY(parseFloat(e.target.value));
                      setWmPosition("custom");
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="settings-actions-footer" style={{ marginTop: "20px" }}>
        <button
          type="button"
          className="settings-save-btn"
          onClick={handleSaveWatermark}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Watermark Settings"}
        </button>
      </div>
    </div>
  );
};

export default WatermarkSettingsTab;
